import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useContext,
} from "react";
import { Modal } from "@clarkinc/zest-design-system";
import { FacetNavigation, SectionGroup } from "../Sidebar/Filters";
import getSelectedFiltersByOrder from "./getSelectedFiltersByOrder";
import hasMoreThanOneSelectedFilters from "./hasMoreThanOneSelectedFilters";
import QuickFilter from "./index";
import {
  getNoFiltersUrl,
  getParamValueFromUrl,
  removeParamFromUrl,
} from "../Sidebar/FilterUrlHelpers";
import FilterChip from "./FilterChip";
import { CompatibleModel } from "~/common/Store/models/Category";
import { FilterModalContext } from "../FilterModalContextProvider";

interface QuickFilterProps {
  facetNavigation: FacetNavigation;
  isLeafCategoryPage: boolean;
  currentUrl: string;
  compatibleModels?: CompatibleModel[];
  setIsFilterRemoved?: (isCleared: boolean) => void;
}

const Container = ({
  facetNavigation,
  isLeafCategoryPage,
  currentUrl,
  compatibleModels = [],
  setIsFilterRemoved,
}: QuickFilterProps) => {
  const noFiltersUrl = getNoFiltersUrl(currentUrl, ["order", "multi"]);
  const listRef = useRef<HTMLUListElement>(null);
  const [isOverflowing, setIsOverFlowing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const quickFilterButtonClass =
    "text-sm bg-transparent border-none cursor-pointer font-semibold text-green-500 underline";

  const { compareProductsActive, storedFilterUrl } =
    useContext(FilterModalContext);

  const shouldOpenModal = compareProductsActive && storedFilterUrl;

  const removeAllFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (shouldOpenModal) {
      Modal.open("confirm-edit-filters-warning");

      storedFilterUrl.current.url = noFiltersUrl;
    } else {
      window.location.href = noFiltersUrl;
      setShowAll(false);
    }
  };

  const showClearFilter = hasMoreThanOneSelectedFilters(
    facetNavigation,
    isLeafCategoryPage,
    currentUrl,
  );

  const checkOverflow = useCallback(
    (elem: React.RefObject<HTMLUListElement>) => {
      if (elem.current && elem.current.parentElement) {
        const elemWidth = elem.current?.getBoundingClientRect().width;
        const parentWidth =
          elem.current?.parentElement?.getBoundingClientRect().width - 158;
        return elemWidth > parentWidth;
      }
      return false;
    },
    [],
  );

  useEffect(() => {
    setIsOverFlowing(checkOverflow(listRef));
  }, [checkOverflow]);

  useEffect(() => {
    const listHeight = listRef.current?.getBoundingClientRect().height;
    if (listHeight && listHeight <= 36) {
      setShowAll(false);
    }
  }, []);

  const displayManufacturerModelFilterChipsIfApplied = () => {
    const manufacturer = getParamValueFromUrl(currentUrl, "manufacturer");
    const model = getParamValueFromUrl(currentUrl, "model");

    const getFilterUrl = (key: string) => {
      let pageUrl = currentUrl;
      pageUrl = removeParamFromUrl(pageUrl, key);
      if (key === "manufacturer") {
        pageUrl = removeParamFromUrl(pageUrl, "model");
      }
      return pageUrl;
    };

    const getModelName = (manufacturer: string, model: string) => {
      const selectedModel =
        compatibleModels && compatibleModels.length > 0
          ? compatibleModels
              .find((x) => x.manufacturer === manufacturer)
              ?.models.find((x) => x.equipmentItemNumber === model)
          : undefined;
      return selectedModel?.name ?? model;
    };

    return (
      <>
        {!!manufacturer && (
          <span className="mr-2">
            <FilterChip
              filterName="Manufacturer"
              filterValue={manufacturer}
              filterUrl={getFilterUrl("manufacturer")}
              setIsFilterRemoved={setIsFilterRemoved}
            />
          </span>
        )}

        {!!manufacturer && !!model && (
          <span className="mr-2">
            <FilterChip
              filterName="Model"
              filterValue={getModelName(manufacturer, model)}
              filterUrl={getFilterUrl("model")}
              setIsFilterRemoved={setIsFilterRemoved}
            />
          </span>
        )}
      </>
    );
  };

  return (
    <section
      aria-label="selected filters"
      className={`bg-white border-solid border-gray-200 border border-t-0 relative flex pl-1.5 md:overflow-x-hidden overflow-x-scroll invisible-scrollbar invisible-scrollbar-firefox ${
        showAll ? "items-start h-auto pb-2" : "items-center h-11"
      }`}
      style={{ zIndex: "3" }}
      data-component="filterContainer"
      data-testid="quick-filter"
    >
      <ul
        className={`flex flex-shrink-0 md:shrink items-center list-none m-0 ${
          !showAll ? "md:overflow-x-hidden" : "flex-wrap"
        }`}
        ref={listRef}
      >
        {displayManufacturerModelFilterChipsIfApplied()}
        {getSelectedFiltersByOrder(facetNavigation, isLeafCategoryPage).map(
          (x: SectionGroup[] | undefined) => (
            <QuickFilter
              filters={x}
              itemClass={showAll ? "mt-2" : ""}
              setIsFilterRemoved={setIsFilterRemoved}
            />
          ),
        )}
        {showClearFilter && showAll && (
          <span
            className={`bg-none flex md:flex-shrink-0 list-none ${
              showAll ? "mt-2" : ""
            }`}
          >
            <button
              data-testid="clear-filters"
              onClick={removeAllFilters}
              type="button"
              className={quickFilterButtonClass}
            >
              Clear Filters
            </button>
            <button
              data-testid="show-fewer"
              onClick={() => setShowAll(false)}
              type="button"
              className={quickFilterButtonClass}
            >
              Show Fewer
            </button>
          </span>
        )}
      </ul>
      <>
        {showClearFilter && !showAll && (
          <span
            className={`flex items-center flex-shrink-0 self-center h-full px-1 ${
              isOverflowing
                ? "md:ml-auto md:border-l md:border-r-0 md:border-t-0 md:border-b-0 md:border-solid md:border-gray-200"
                : ""
            }`}
          >
            <span className="bg-none flex md:flex-shrink-0 list-none">
              <button
                data-testid="clear-filters"
                onClick={removeAllFilters}
                type="button"
                className={quickFilterButtonClass}
              >
                Clear Filters
              </button>
              {isOverflowing && (
                <button
                  data-testid="show-all"
                  onClick={() => setShowAll(true)}
                  type="button"
                  className={`${quickFilterButtonClass} hidden md:block`}
                >
                  Show All
                </button>
              )}
            </span>
          </span>
        )}
      </>
    </section>
  );
};

export default Container;
